import type { Vec3, PlanetState, ProjectileState, CollisionResult, PlayerState, AirborneCause } from '../types';
import { vec3, add, sub, scale, addScaled, normalize, length, lengthSq, clone } from '../math';
import { G, PHYS_DT, SPACE_DEATH_DISTANCE, SPACE_DEATH_RADII, MAX_AIRBORNE_TIME, FALL_DAMAGE_FACTOR, LANDING_VELOCITY_THRESHOLD, WORMHOLE_SPEED_BOOST, MAX_WORMHOLE_TRANSITS, WORMHOLE_COOLDOWN_DIST, MEGAPLANET_THRUST_RADIUS, MEGAPLANET_POWER_DIVISOR, MAX_TERRAIN_HEIGHT_FRACTION } from '../constants';
import { getSurfaceRadius, getSurfaceRadiusRaw } from './surface';
import { damageUnit, killUnit } from './damage';

// =============================================================================
// GRAVITY
// =============================================================================

/**
 * Compute gravitational acceleration at a point from all planets.
 * Pure function — no side effects.
 */
export function gravityAccel(pos: Vec3, planets: PlanetState[]): Vec3 {
  let ax = 0, ay = 0, az = 0;

  for (const planet of planets) {
    if (!planet.alive) continue;

    const dx = planet.position.x - pos.x;
    const dy = planet.position.y - pos.y;
    const dz = planet.position.z - pos.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    const dist = Math.sqrt(distSq);

    if (dist < 0.5) continue;

    // Soften at close range to prevent numerical explosion
    const effectiveDist = Math.max(dist, planet.radius * 0.5);
    const forceMag = G * planet.mass / (effectiveDist * effectiveDist);
    const invDist = 1 / dist;

    ax += dx * invDist * forceMag;
    ay += dy * invDist * forceMag;
    az += dz * invDist * forceMag;
  }

  return vec3(ax, ay, az);
}

// =============================================================================
// MEGAPLANET POWER SCALING
// =============================================================================

/**
 * Returns true if a planet is large enough to activate megaplanet power scaling.
 */
export function isMegaplanetLaunch(planetRadius: number): boolean {
  return planetRadius >= MEGAPLANET_THRUST_RADIUS;
}

/**
 * Power multiplier for megaplanet launches.
 * Scales launch velocity proportionally to planet radius so projectiles
 * can escape the stronger gravity. Returns 1 for small planets.
 */
export function megaPlanetPowerMult(planetRadius: number): number {
  if (!isMegaplanetLaunch(planetRadius)) return 1;
  return planetRadius / MEGAPLANET_POWER_DIVISOR;
}

// =============================================================================
// RK4 INTEGRATOR
// =============================================================================

export interface IntegrationResult {
  pos: Vec3;
  vel: Vec3;
}

/**
 * Single RK4 step for gravity-only motion.
 */
export function rk4Step(
  pos: Vec3,
  vel: Vec3,
  dt: number,
  planets: PlanetState[],
  /** Felt gravity (WORKBOARD §8.1): a person feels g × this; 1 (the default) is today's integrator, untouched. */
  gravityScale = 1,
): IntegrationResult {
  if (gravityScale !== 1) return rk4StepScaled(pos, vel, dt, planets, gravityScale);
  const k1v = gravityAccel(pos, planets);
  const k1x = vel;

  const p2 = addScaled(pos, k1x, dt / 2);
  const v2 = addScaled(vel, k1v, dt / 2);
  const k2v = gravityAccel(p2, planets);
  const k2x = v2;

  const p3 = addScaled(pos, k2x, dt / 2);
  const v3 = addScaled(vel, k2v, dt / 2);
  const k3v = gravityAccel(p3, planets);
  const k3x = v3;

  const p4 = addScaled(pos, k3x, dt);
  const v4 = addScaled(vel, k3v, dt);
  const k4v = gravityAccel(p4, planets);
  const k4x = v4;

  const newPos = vec3(
    pos.x + (k1x.x + 2 * k2x.x + 2 * k3x.x + k4x.x) * dt / 6,
    pos.y + (k1x.y + 2 * k2x.y + 2 * k3x.y + k4x.y) * dt / 6,
    pos.z + (k1x.z + 2 * k2x.z + 2 * k3x.z + k4x.z) * dt / 6,
  );
  const newVel = vec3(
    vel.x + (k1v.x + 2 * k2v.x + 2 * k3v.x + k4v.x) * dt / 6,
    vel.y + (k1v.y + 2 * k2v.y + 2 * k3v.y + k4v.y) * dt / 6,
    vel.z + (k1v.z + 2 * k2v.z + 2 * k3v.z + k4v.z) * dt / 6,
  );

  return { pos: newPos, vel: newVel };
}

/** rk4Step under g × `gs` (a person's felt gravity). Kept apart so the classic integrator stays bit-identical. */
function rk4StepScaled(pos: Vec3, vel: Vec3, dt: number, planets: PlanetState[], gs: number): IntegrationResult {
  const acc = (p: Vec3): Vec3 => { const a = gravityAccel(p, planets); return vec3(a.x * gs, a.y * gs, a.z * gs); };
  const k1v = acc(pos), k1x = vel;
  const k2v = acc(addScaled(pos, k1x, dt / 2)), k2x = addScaled(vel, k1v, dt / 2);
  const k3v = acc(addScaled(pos, k2x, dt / 2)), k3x = addScaled(vel, k2v, dt / 2);
  const k4v = acc(addScaled(pos, k3x, dt)), k4x = addScaled(vel, k3v, dt);
  return {
    pos: vec3(
      pos.x + (k1x.x + 2 * k2x.x + 2 * k3x.x + k4x.x) * dt / 6,
      pos.y + (k1x.y + 2 * k2x.y + 2 * k3x.y + k4x.y) * dt / 6,
      pos.z + (k1x.z + 2 * k2x.z + 2 * k3x.z + k4x.z) * dt / 6,
    ),
    vel: vec3(
      vel.x + (k1v.x + 2 * k2v.x + 2 * k3v.x + k4v.x) * dt / 6,
      vel.y + (k1v.y + 2 * k2v.y + 2 * k3v.y + k4v.y) * dt / 6,
      vel.z + (k1v.z + 2 * k2v.z + 2 * k3v.z + k4v.z) * dt / 6,
    ),
  };
}

// =============================================================================
// COLLISION DETECTION
// =============================================================================

// Surface queries live in core/surface.ts (exact mesh intersection + spatial
// index). Re-exported here so existing imports keep working.
export { getSurfaceRadius, getSurfacePoint } from './surface';

/**
 * Check if a point collides with any planet's terrain surface.
 * Bounding-shell rejection first, then exact mesh lookup (cheap via the
 * spatial index). `ignorePlanetIndex` skips one planet (wormhole cooldown).
 */
export function checkCollision(pos: Vec3, planets: PlanetState[], ignorePlanetIndex = -1): CollisionResult | null {
  for (let i = 0; i < planets.length; i++) {
    const planet = planets[i];
    if (!planet.alive || i === ignorePlanetIndex) continue;

    const dx = pos.x - planet.position.x;
    const dy = pos.y - planet.position.y;
    const dz = pos.z - planet.position.z;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // Nothing of the terrain lies beyond the bounding shell
    if (dist > planet.radius * (1 + MAX_TERRAIN_HEIGHT_FRACTION)) continue;

    const surfR = getSurfaceRadiusRaw(planet.heightmap, planet.basePositions, planet.radius, dx, dy, dz);
    if (dist <= surfR) {
      const inv = dist > 1e-12 ? surfR / dist : 0;
      return {
        planetIndex: i,
        point: { x: planet.position.x + dx * inv, y: planet.position.y + dy * inv, z: planet.position.z + dz * inv },
      };
    }
  }
  return null;
}

/**
 * Fast collision check for trajectory preview - uses simple sphere, no heightmap.
 */
export function checkCollisionFast(pos: Vec3, planets: PlanetState[]): CollisionResult | null {
  for (let i = 0; i < planets.length; i++) {
    const planet = planets[i];
    if (!planet.alive) continue;

    const diff = sub(pos, planet.position);
    const dist = length(diff);

    // Use base radius for fast check (slightly conservative)
    if (dist <= planet.radius * 0.95) {
      const surfPoint = addScaled(planet.position, normalize(diff), planet.radius);
      return { planetIndex: i, point: surfPoint };
    }
  }
  return null;
}

// =============================================================================
// PLAYER HITBOX COLLISION
// =============================================================================

export interface PlayerCollisionResult {
  playerId: number;
  planetIndex: number;
  surfacePoint: Vec3;
}

export const PLAYER_HITBOX_RADIUS = 0.6;

/**
 * Check if a projectile position hits any player's body hitbox.
 * Hitbox is a sphere centered 0.5 units above the player's surface position.
 * Skips dead, airborne, and owner players.
 */
export function checkPlayerCollision(
  pos: Vec3,
  players: PlayerState[],
  planets: PlanetState[],
  ownerId: number
): PlayerCollisionResult | null {
  const hitRadiusSq = PLAYER_HITBOX_RADIUS * PLAYER_HITBOX_RADIUS;

  for (const player of players) {
    if (!player.alive || player.airborne || player.id === ownerId) continue;

    const planet = planets[player.planetIndex];
    if (!planet || !planet.alive) continue;

    // Compute player world position on planet surface
    const dir = player.surfaceDir;
    const dirLen = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
    if (dirLen < 0.001) continue;
    const nx = dir.x / dirLen, ny = dir.y / dirLen, nz = dir.z / dirLen;
    const surfR = getSurfaceRadius(planet, dir);
    const surfX = planet.position.x + nx * surfR;
    const surfY = planet.position.y + ny * surfR;
    const surfZ = planet.position.z + nz * surfR;

    // Hitbox center is 0.5 units above surface (body center of mass)
    const cx = surfX + nx * 0.5;
    const cy = surfY + ny * 0.5;
    const cz = surfZ + nz * 0.5;

    const dx = pos.x - cx;
    const dy = pos.y - cy;
    const dz = pos.z - cz;
    const distSq = dx * dx + dy * dy + dz * dz;

    if (distSq <= hitRadiusSq) {
      return {
        playerId: player.id,
        planetIndex: player.planetIndex,
        surfacePoint: vec3(surfX, surfY, surfZ),
      };
    }
  }

  return null;
}

// =============================================================================
// WORMHOLE TRANSIT
// =============================================================================

export interface WormholeTransitResult {
  transited: boolean;
  exitPos?: Vec3;
  exitVel?: Vec3;
  exitWormholeId?: number;
  transits?: number;
  maxTransitsReached?: boolean;
}

/**
 * Check if a position is inside a wormhole and compute transit to linked wormhole.
 * Pure function — no side effects.
 */
export function checkWormholeTransit(
  pos: Vec3,
  vel: Vec3,
  planets: PlanetState[],
  lastWormholeId: number | null,
  currentTransits: number
): WormholeTransitResult {
  for (const planet of planets) {
    if (planet.type !== 'wormhole' || !planet.alive) continue;
    if (planet.id === lastWormholeId) continue; // cooldown — skip exit wormhole

    const diff = sub(pos, planet.position);
    const dist = length(diff);

    if (dist < planet.radius) {
      // Inside wormhole — find linked partner
      if (planet.linkedWormholeId == null) continue;
      const linked = planets.find(p => p.id === planet.linkedWormholeId);
      if (!linked) continue;

      const newTransits = currentTransits + 1;
      if (newTransits >= MAX_WORMHOLE_TRANSITS) {
        return {
          transited: true,
          exitWormholeId: linked.id,
          transits: newTransits,
          maxTransitsReached: true,
        };
      }

      // Exit position: linked wormhole position + direction of travel * 1.5 radius
      const speed = length(vel);
      const exitDir = speed > 0.001 ? normalize(vel) : { x: 1, y: 0, z: 0 };
      const exitPos = addScaled(linked.position, exitDir, linked.radius * 1.5);
      const exitVel = scale(exitDir, speed * WORMHOLE_SPEED_BOOST);

      return {
        transited: true,
        exitPos,
        exitVel,
        exitWormholeId: linked.id,
        transits: newTransits,
        maxTransitsReached: false,
      };
    }
  }

  return { transited: false };
}

/**
 * Check if cooldown should be cleared (far enough from the exit wormhole).
 */
export function shouldClearWormholeCooldown(
  pos: Vec3,
  lastWormholeId: number | null,
  planets: PlanetState[]
): boolean {
  if (lastWormholeId == null) return false;
  const wormhole = planets.find(p => p.id === lastWormholeId);
  if (!wormhole) return true; // wormhole gone, clear cooldown
  const dist = length(sub(pos, wormhole.position));
  return dist > wormhole.radius * WORMHOLE_COOLDOWN_DIST;
}

// =============================================================================
// AIRBORNE PLAYER PHYSICS
// =============================================================================

export interface AirborneUpdateResult {
  player: PlayerState;
  landed: boolean;
  landedPlanetIndex: number | null;
  landingDamage: number;
  diedInSpace: boolean;
  diedInStar: boolean;
  diedInWormhole: boolean;
  teleported: boolean;
}

/**
 * Update an airborne player's position and velocity under gravity.
 * Checks for landing on any planet or death in space.
 */
/**
 * Landing damage for an impact speed: linear above the soft-landing threshold,
 * scaled by the match multiplier. The one formula for the game, the headless
 * harness and the bots' landing judgement.
 */
export function landingDamageFor(impactSpeed: number, fallDamageMultiplier = 1.0): number {
  if (fallDamageMultiplier <= 0 || impactSpeed <= LANDING_VELOCITY_THRESHOLD) return 0;
  return (impactSpeed - LANDING_VELOCITY_THRESHOLD) * FALL_DAMAGE_FACTOR * 10 * fallDamageMultiplier;
}

/** The multiplier for a landing: the match multiplier times the per-cause one (both default 1). */
export function fallDamageMultiplierFor(
  cause: AirborneCause | null | undefined,
  fallDamageMultiplier = 1.0,
  byCause?: Partial<Record<AirborneCause, number>>,
): number {
  if (cause === 'struck') return 0;   // 63.19: a vehicle's blow was counted when it struck (core/vehicleImpact.ts); the landing adds none
  const causeMult = cause && byCause ? (byCause[cause] ?? 1.0) : 1.0;
  return fallDamageMultiplier * causeMult;
}

/**
 * Is a body at `pos` lost in space: past every live planet's reach — SPACE_DEATH_DISTANCE from its centre, or
 * SPACE_DEATH_RADII of its radii when further (a megaplanet's ground stands past 80)?
 */
export function lostInSpace(pos: Vec3, planets: readonly PlanetState[]): boolean {
  for (const planet of planets) {
    if (!planet.alive) continue;
    if (length(sub(pos, planet.position)) <= Math.max(SPACE_DEATH_DISTANCE, SPACE_DEATH_RADII * planet.radius)) return false;
  }
  return true;
}

export function updateAirbornePlayer(
  player: PlayerState,
  planets: PlanetState[],
  dt: number,
  fallDamageMultiplier = 1.0,
  fallDamageByCause?: Partial<Record<AirborneCause, number>>,
  /** Felt gravity for a person on the action clock (1 = the planet's g: every turn-game unit). */
  gravityScale = 1,
  /** S3: the ground a person lands on (the fine ground at person scale); absent = the mesh (checkCollision). */
  groundHit?: (pos: Vec3) => CollisionResult | null,
  /**
   * 62.8 (SCALE.md §6, launch and landing): a person's landing is judged in the person family — the soft-landing
   * threshold × k and the damage per unit of excess speed ÷ k, i.e. the speed ÷ k against today's rule. 1 = today.
   */
  landingScale = 1,
): AirborneUpdateResult {
  if (!player.airborne || !player.airbornePos || !player.airborneVel) {
    return {
      player,
      landed: false,
      landedPlanetIndex: null,
      landingDamage: 0,
      diedInSpace: false,
      diedInStar: false,
      diedInWormhole: false,
      teleported: false,
    };
  }

  // Integrate position under gravity
  const result = rk4Step(player.airbornePos, player.airborneVel, dt, planets, gravityScale);
  const newPos = result.pos;
  const newVel = result.vel;

  // Clear wormhole cooldown when far enough from exit
  let currentLastWormholeId = player.lastWormholeId ?? null;
  if (shouldClearWormholeCooldown(newPos, currentLastWormholeId, planets)) {
    currentLastWormholeId = null;
  }

  // Check for landing on any planet
  const collision = groundHit ? groundHit(newPos) : checkCollision(newPos, planets);
  if (collision) {
    const planet = planets[collision.planetIndex];

    // Star contact = instant death
    if (planet.type === 'star') {
      return {
        player: {
          ...killUnit(player, 'star'),
          airborne: false,
          airbornePos: null,
          airborneVel: null,
        },
        landed: false,
        landedPlanetIndex: collision.planetIndex,
        landingDamage: 0,
        diedInSpace: false,
        diedInStar: true,
        diedInWormhole: false,
        teleported: false,
      };
    }

    // Wormhole contact = teleport to linked wormhole
    if (planet.type === 'wormhole') {
      const transit = checkWormholeTransit(
        newPos, newVel, planets,
        currentLastWormholeId,
        player.wormholeTransits ?? 0
      );

      if (transit.transited) {
        if (transit.maxTransitsReached) {
          return {
            player: {
              ...killUnit(player, 'wormhole'),
              airborne: false,
              airbornePos: null,
              airborneVel: null,
            },
            landed: false,
            landedPlanetIndex: null,
            landingDamage: 0,
            diedInSpace: false,
            diedInStar: false,
            diedInWormhole: true,
            teleported: false,
          };
        }

        return {
          player: {
            ...player,
            airbornePos: transit.exitPos!,
            airborneVel: transit.exitVel!,
            lastWormholeId: transit.exitWormholeId!,
            wormholeTransits: transit.transits!,
          },
          landed: false,
          landedPlanetIndex: null,
          landingDamage: 0,
          diedInSpace: false,
          diedInStar: false,
          diedInWormhole: false,
          teleported: true,
        };
      }

      // Cooldown prevented transit — just pass through
      return {
        player: {
          ...player,
          airbornePos: newPos,
          airborneVel: newVel,
          lastWormholeId: currentLastWormholeId,
        },
        landed: false,
        landedPlanetIndex: null,
        landingDamage: 0,
        diedInSpace: false,
        diedInStar: false,
        diedInWormhole: false,
        teleported: false,
      };
    }

    const impactSpeed = length(newVel);

    // Landing damage from impact speed, scaled by the match multiplier and the per-cause one
    // The unit's own jump landings are softened by its kit (padding, equipment); knockbacks and hazards are not
    const kitMult = player.airborneCause === 'jump' ? (player.modifiers?.jumpFallDamageMult ?? 1) : 1;
    const landingDamage = landingDamageFor(
      landingScale === 1 ? impactSpeed : impactSpeed / landingScale, fallDamageMultiplierFor(player.airborneCause, fallDamageMultiplier, fallDamageByCause) * kitMult,
    );

    // Calculate surface direction for landing
    const surfaceDir = normalize(sub(collision.point, planet.position));

    return {
      player: {
        ...damageUnit(player, landingDamage, 'landing').unit,
        airborne: false,
        airbornePos: null,
        airborneVel: null,
        airborneCause: null,
        airborneTime: 0,
        planetIndex: collision.planetIndex,
        surfaceDir,
        lastWormholeId: null,
        wormholeTransits: 0,
      },
      landed: true,
      landedPlanetIndex: collision.planetIndex,
      landingDamage,
      diedInSpace: false,
      diedInStar: false,
      diedInWormhole: false,
      teleported: false,
    };
  }

  // Check for death in space (too far from all planets), or for an orbit that
  // never comes down: past MAX_AIRBORNE_TIME the unit counts as lost
  const airborneTime = (player.airborneTime ?? 0) + dt;

  if (lostInSpace(newPos, planets) || airborneTime > MAX_AIRBORNE_TIME) {
    return {
      player: killUnit(player, 'space'),
      landed: false,
      landedPlanetIndex: null,
      landingDamage: 0,
      diedInSpace: true,
      diedInStar: false,
      diedInWormhole: false,
      teleported: false,
    };
  }

  // Still airborne, update position/velocity
  return {
    player: {
      ...player,
      airbornePos: newPos,
      airborneVel: newVel,
      airborneTime,
      lastWormholeId: currentLastWormholeId,
    },
    landed: false,
    landedPlanetIndex: null,
    landingDamage: 0,
    diedInSpace: false,
    diedInStar: false,
    diedInWormhole: false,
    teleported: false,
  };
}

/**
 * Check if any players are currently airborne.
 */
export function hasAirbornePlayers(players: PlayerState[]): boolean {
  return players.some(p => p.alive && p.airborne);
}

// =============================================================================
// BALLISTIC JUMP
// =============================================================================

/**
 * Simulate a player jump trajectory for preview.
 * Returns array of positions and whether it lands on a planet.
 */
export function simulateJumpTrajectory(
  startPos: Vec3,
  launchVel: Vec3,
  planets: PlanetState[],
  maxSteps = 600,
  dt?: number
): { points: Vec3[]; landingPlanetIndex: number | null; landingPoint: Vec3 | null; landingSpeed: number | null } {
  const points: Vec3[] = [clone(startPos)];
  const previewDt = dt ?? PHYS_DT * 2;

  let pos = clone(startPos);
  let vel = clone(launchVel);
  let jumpLastWormholeId: number | null = null;
  let jumpTransits = 0;

  for (let i = 0; i < maxSteps; i++) {
    const result = rk4Step(pos, vel, previewDt, planets);
    pos = result.pos;
    vel = result.vel;
    points.push(clone(pos));

    // Clear wormhole cooldown
    if (shouldClearWormholeCooldown(pos, jumpLastWormholeId, planets)) {
      jumpLastWormholeId = null;
    }

    // Check for collision with any planet
    const collision = checkCollision(pos, planets);
    if (collision) {
      const hitPlanet = planets[collision.planetIndex];
      if (hitPlanet.type === 'wormhole') {
        const transit = checkWormholeTransit(pos, vel, planets, jumpLastWormholeId, jumpTransits);
        if (transit.transited && !transit.maxTransitsReached) {
          pos = transit.exitPos!;
          vel = transit.exitVel!;
          jumpLastWormholeId = transit.exitWormholeId!;
          jumpTransits = transit.transits!;
          points.push(clone(pos));
          continue;
        }
      }
      return {
        points,
        landingPlanetIndex: collision.planetIndex,
        landingPoint: collision.point,
        landingSpeed: length(vel),
      };
    }

    // Check if too far from all planets (lost in space)
    if (lostInSpace(pos, planets)) {
      return { points, landingPlanetIndex: null, landingPoint: null, landingSpeed: null };
    }
  }

  // Didn't land within max steps
  return { points, landingPlanetIndex: null, landingPoint: null, landingSpeed: null };
}
